import { ROUTES } from '@/lib/constants/routes';
import { EVENTS_STRINGS } from './constants/events-strings';

/**
 * Router state a link sets when it opens an event's detail page from somewhere
 * other than the events page, so the page's back link returns there instead of
 * to the generic events list.
 */
export interface EventDetailLinkState {
  backTo: 'dashboard';
}

/** State for links on the member dashboard. */
export const FROM_DASHBOARD: EventDetailLinkState = { backTo: 'dashboard' };

export interface EventBackLink {
  to: string;
  label: string;
}

const BACK_TO_DASHBOARD: EventBackLink = {
  to: ROUTES.DASHBOARD,
  label: EVENTS_STRINGS.DETAIL.BACK_TO_DASHBOARD,
};
const BACK_TO_EVENTS: EventBackLink = {
  to: ROUTES.EVENTS,
  label: EVENTS_STRINGS.DETAIL.BACK_TO_EVENTS,
};
const BACK_TO_HOME: EventBackLink = {
  to: ROUTES.HOME,
  label: EVENTS_STRINGS.DETAIL.BACK_TO_HOME,
};

/**
 * Pick the detail page's back link from the router state it was opened with.
 * Anything unrecognised (a direct visit, a refresh in a new tab, a bookmark)
 * falls back to the events list - or, for a logged-out viewer, to Home.
 *
 * The viewer matters because both default destinations are member pages: the
 * events list sits behind the auth guard, so offering it to the anonymous
 * visitor who arrived from the public Home events section would bounce them to
 * login one click after we stopped bouncing them on arrival. Home is where that
 * visitor came from, and the only one of the three they can actually open.
 *
 * @param state - `location.state` as React Router hands it over (untyped)
 * @param isAuthenticated - whether the viewer has a session
 * @returns where the back link goes and what it says
 */
export function resolveEventBackLink(state: unknown, isAuthenticated = true): EventBackLink {
  if (!isAuthenticated) return BACK_TO_HOME;
  const backTo = (state as Partial<EventDetailLinkState> | null)?.backTo;
  return backTo === 'dashboard' ? BACK_TO_DASHBOARD : BACK_TO_EVENTS;
}
